/**
 * Deep Link Handler Service
 * 
 * Handles deep link parameters from landing pages and applies them to the composer.
 * Supports template loading, remix track loading, BPM/key setting, and UTM parameter tracking.
 * 
 * @module services/deepLink/DeepLinkHandler
 */

import { templateService } from '../templates/TemplateService';
import { shareAPI } from '../api/ShareAPI';
import type { DeepLinkParams } from '@/types/landingPage';
import type { Composition } from '@/types/composition';
import type { Template } from '@/types/template';

/**
 * Result of deep link handling
 */
export interface DeepLinkHandlerResult {
  /** Whether the deep link was successfully handled */
  success: boolean;
  /** Composition to load (if any) */
  composition?: Composition;
  /** Template to apply (if any) */
  template?: Template;
  /** BPM to set (if any) */
  bpm?: number;
  /** Key to set (if any) */
  key?: string;
  /** Error message (if failed) */
  error?: string;
  /** Entry point type for analytics */
  entryPoint: 'remix' | 'template' | 'default';
}

/**
 * UTM parameters for attribution tracking
 */
export interface UTMParameters {
  source?: string;
  campaign?: string;
  medium?: string;
}

/**
 * Deep Link Handler Service
 * 
 * Processes deep link parameters and coordinates with other services
 * to apply the appropriate state to the composer.
 */
class DeepLinkHandler {
  private utmParameters: UTMParameters = {};

  /**
   * Handle deep link parameters
   * 
   * Priority order:
   * 1. Remix (if present, ignore template)
   * 2. Template (if present and no remix)
   * 3. BPM/Key (applied regardless)
   * 
   * @param params - Deep link parameters from URL
   * @returns Result of handling the deep link
   */
  async handleDeepLink(params: DeepLinkParams): Promise<DeepLinkHandlerResult> {
    console.log('[DeepLinkHandler] Processing deep link parameters:', params);

    // Store UTM parameters for attribution
    if (params.utm_source || params.utm_campaign || params.utm_medium) {
      const utmParams: UTMParameters = {};
      if (params.utm_source) utmParams.source = params.utm_source;
      if (params.utm_campaign) utmParams.campaign = params.utm_campaign;
      if (params.utm_medium) utmParams.medium = params.utm_medium;
      this.storeUTMParameters(utmParams);
    }

    // Priority 1: Handle remix parameter
    if (params.remix) {
      return await this.handleRemix(params);
    }

    // Priority 2: Handle template parameter
    if (params.template) {
      return this.handleTemplate(params);
    }

    // Priority 3: Handle BPM/Key only
    if (params.bpm || params.key) {
      return this.handleBPMKey(params);
    }

    // No parameters to handle
    console.log('[DeepLinkHandler] No deep link parameters to process');
    return {
      success: true,
      entryPoint: 'default',
    };
  }

  /**
   * Handle remix deep link
   * Loads a shared composition by track ID
   * 
   * @param params - Deep link parameters
   * @returns Handler result with composition
   */
  private async handleRemix(params: DeepLinkParams): Promise<DeepLinkHandlerResult> {
    console.log('[DeepLinkHandler] Handling remix:', params.remix);

    try {
      // Load the shared composition
      const composition = await shareAPI.load(params.remix!);

      // Apply BPM/Key overrides if provided
      if (params.bpm) {
        composition.tempo = params.bpm;
      }
      if (params.key) {
        // Note: Key is not currently stored in composition, but we return it
        // for the caller to handle
      }

      // Emit composer session start event
      this.emitComposerSessionStart('remix');

      console.log('[DeepLinkHandler] Remix loaded successfully');
      return {
        success: true,
        composition,
        ...(params.bpm !== undefined && { bpm: params.bpm }),
        ...(params.key !== undefined && { key: params.key }),
        entryPoint: 'remix',
      };
    } catch (error) {
      console.error('[DeepLinkHandler] Failed to load remix:', error);
      
      // Graceful fallback - return error but don't block user
      return {
        success: false,
        error: `Failed to load remix track: ${error instanceof Error ? error.message : 'Unknown error'}`,
        entryPoint: 'remix',
      };
    }
  }

  /**
   * Handle template deep link
   * Loads a template by name
   * 
   * @param params - Deep link parameters
   * @returns Handler result with template
   */
  private handleTemplate(params: DeepLinkParams): DeepLinkHandlerResult {
    console.log('[DeepLinkHandler] Handling template:', params.template);

    try {
      // Find template by ID
      const template = templateService.getTemplateById(params.template!);

      if (!template) {
        console.warn('[DeepLinkHandler] Template not found:', params.template);
        
        // Graceful fallback - return error but don't block user
        return {
          success: false,
          error: `Template "${params.template}" not found`,
          ...(params.bpm !== undefined && { bpm: params.bpm }),
          ...(params.key !== undefined && { key: params.key }),
          entryPoint: 'template',
        };
      }

      // Emit composer session start event
      this.emitComposerSessionStart('template');

      console.log('[DeepLinkHandler] Template loaded successfully');
      return {
        success: true,
        template,
        ...(params.bpm !== undefined && { bpm: params.bpm }),
        ...(params.key !== undefined && { key: params.key }),
        entryPoint: 'template',
      };
    } catch (error) {
      console.error('[DeepLinkHandler] Failed to load template:', error);
      
      return {
        success: false,
        error: `Failed to load template: ${error instanceof Error ? error.message : 'Unknown error'}`,
        entryPoint: 'template',
      };
    }
  }

  /**
   * Handle BPM/Key only deep link
   * 
   * @param params - Deep link parameters
   * @returns Handler result with BPM/Key
   */
  private handleBPMKey(params: DeepLinkParams): DeepLinkHandlerResult {
    console.log('[DeepLinkHandler] Handling BPM/Key:', { bpm: params.bpm, key: params.key });

    // Emit composer session start event
    this.emitComposerSessionStart('default');

    return {
      success: true,
      ...(params.bpm !== undefined && { bpm: params.bpm }),
      ...(params.key !== undefined && { key: params.key }),
      entryPoint: 'default',
    };
  }

  /**
   * Store UTM parameters for attribution
   * 
   * @param params - UTM parameters
   */
  private storeUTMParameters(params: UTMParameters): void {
    this.utmParameters = params;
    console.log('[DeepLinkHandler] Stored UTM parameters:', params);

    // Store in session storage for persistence across page loads
    try {
      sessionStorage.setItem('utm_parameters', JSON.stringify(params));
    } catch (error) {
      console.warn('[DeepLinkHandler] Failed to store UTM parameters in session storage:', error);
    }
  }

  /**
   * Get stored UTM parameters
   * 
   * @returns UTM parameters
   */
  getUTMParameters(): UTMParameters {
    // Try to load from session storage if not in memory
    if (Object.keys(this.utmParameters).length === 0) {
      try {
        const stored = sessionStorage.getItem('utm_parameters');
        if (stored) {
          this.utmParameters = JSON.parse(stored);
        }
      } catch (error) {
        console.warn('[DeepLinkHandler] Failed to load UTM parameters from session storage:', error);
      }
    }

    return this.utmParameters;
  }

  /**
   * Clear stored UTM parameters
   */
  clearUTMParameters(): void {
    this.utmParameters = {};
    try {
      sessionStorage.removeItem('utm_parameters');
    } catch (error) {
      console.warn('[DeepLinkHandler] Failed to clear UTM parameters from session storage:', error);
    }
  }

  /**
   * Emit composer session start event
   * 
   * @param entryPoint - Entry point type
   */
  private emitComposerSessionStart(entryPoint: 'remix' | 'template' | 'default'): void {
    try {
      // Get UTM parameters
      const utm = this.getUTMParameters();

      // Emit analytics event
      // Note: We're using a custom event since composer_session_start is not in the standard analytics events
      // This will be sent as a custom event to GA4
      const eventData = {
        entry_point: entryPoint,
        utm_source: utm.source,
        utm_campaign: utm.campaign,
        utm_medium: utm.medium,
        timestamp: Date.now(),
      };

      console.log('[DeepLinkHandler] Emitting composer_session_start event:', eventData);

      // Send custom event to analytics
      // We'll use the GA4Provider directly since this is a custom event
      if (window.gtag) {
        window.gtag('event', 'composer_session_start', eventData);
      }
    } catch (error) {
      console.warn('[DeepLinkHandler] Failed to emit composer session start event:', error);
    }
  }
}

// Export singleton instance
export const deepLinkHandler = new DeepLinkHandler();
